import React, { useContext, useState, useEffect, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import { Page } from '../types';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { X, Sparkles, ArrowRight } from 'lucide-react';

export const PromoBanner: React.FC = () => {
    const context = useContext(AppContext);
    const [isDismissed, setIsDismissed] = useState(false);
    const bannerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Safe check for session storage to remember dismissal during active session
        const dismissed = sessionStorage.getItem('promo_banner_dismissed');
        if (dismissed === 'true') {
            setIsDismissed(true);
        }
    }, []);

    const footerInfo = context?.footerInfo;
    const isAdmin = context?.isAdmin;
    const setPage = context?.setPage;

    // Do not show banner on admin page or if disabled/empty
    const isVisible = !isAdmin && footerInfo && footerInfo.promo_active && (footerInfo.promo_title || footerInfo.promo_text) && !isDismissed;

    useEffect(() => {
        if (!isVisible) {
            document.documentElement.style.setProperty('--promo-banner-height', '0px');
            return;
        }

        const publishHeight = (height: number) => {
            window.requestAnimationFrame(() => {
                document.documentElement.style.setProperty('--promo-banner-height', `${Math.ceil(height)}px`);
            });
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && bannerRef.current) {
            resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[0];
                if (entry) publishHeight(entry.contentRect.height);
            });
            resizeObserver.observe(bannerRef.current);
        }

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            document.documentElement.style.setProperty('--promo-banner-height', '0px');
        };
    }, [isVisible, footerInfo]);

    if (!isVisible) return null;

    const resolvePageLink = (link: string | undefined): Page | null => {
        if (!link) return null;
        const normalized = link.toLowerCase().trim();
        if (normalized.includes('repar') || normalized.includes('service')) return Page.Repairs;
        if (normalized.includes('achat') || normalized.includes('acheter') || normalized.includes('buy')) return Page.BuyPhone;
        if (normalized.includes('rachat') || normalized.includes('revendre') || normalized.includes('sell')) return Page.SellPhone;
        if (normalized.includes('access')) return Page.Accessories;
        if (normalized.includes('blog')) return Page.Blog;
        if (normalized.includes('imei')) return Page.ImeiVerification;
        if (normalized.includes('contact')) return Page.Contact;
        if (normalized.includes('term')) return Page.Terms;
        if (normalized === '/' || normalized.includes('home') || normalized.includes('accueil')) return Page.Home;
        return null;
    };

    const handleActionClick = () => {
        const target = resolvePageLink(footerInfo?.promo_link);
        if (target && setPage) {
            setPage(target);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (footerInfo?.promo_link) {
            // If it's an external URL or custom path
            if (footerInfo.promo_link.startsWith('http')) {
                window.open(footerInfo.promo_link, '_blank', 'noopener,noreferrer');
            } else {
                window.location.hash = footerInfo.promo_link;
            }
        }
    };

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDismissed(true);
        sessionStorage.setItem('promo_banner_dismissed', 'true');
        document.documentElement.style.setProperty('--promo-banner-height', '0px');
    };

    const hasBgImage = !!footerInfo?.promo_image;

    // Premium styling variants based on image presence or custom brand rules
    return (
        <AnimatePresence>
            <motion.div
                ref={bannerRef}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="sticky top-0 w-full overflow-hidden select-none z-[5000] border-b border-white/10"
                id="global-promo-banner"
            >
                {/* Background Representation */}
                {hasBgImage ? (
                    <div className="absolute inset-0 w-full h-full">
                        <img 
                            src={footerInfo.promo_image} 
                            alt={footerInfo.promo_title || "Promotion Banner"} 
                            className="w-full h-full object-cover object-center scale-105 filter blur-[2px] brightness-[0.35]" 
                            referrerPolicy="no-referrer"
                            width="1920"
                            height="80"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/80 via-black/90 to-indigo-950/80 blend-multiply" />
                    </div>
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900 via-slate-900 to-indigo-900 animate-gradient-xy" />
                )}

                {/* Animated micro-decoration subtle grid overlay */}
                <div className="absolute inset-0 opacity-15 pointer-events-none bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:16px_16px]" />

                {/* Light reflection effect */}
                <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-blue-400/40 to-transparent shadow-[0_0_8px_rgba(59,130,246,0.5)]" />

                <div 
                    onClick={handleActionClick} 
                    className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 sm:py-3 flex flex-wrap sm:flex-nowrap items-center justify-between gap-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                >
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="shrink-0 w-8 h-8 rounded-full bg-blue-500/20 border border-blue-400/30 flex items-center justify-center animate-pulse shadow-inner">
                            <Sparkles className="w-4 h-4 text-blue-400" />
                        </div>
                        
                        <div className="flex-1 space-y-0.5 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                            {footerInfo.promo_title && (
                                <span className="inline-block text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-white bg-blue-500/90 text-white px-2 py-0.5 rounded-[4px] shadow-sm">
                                    {footerInfo.promo_title}
                                </span>
                            )}
                            {footerInfo.promo_text && (
                                <p className="text-xs font-bold text-slate-100 line-clamp-1 sm:line-clamp-none tracking-wide">
                                    {footerInfo.promo_text}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto ml-auto sm:ml-0">
                        {/* Interactive elegant action button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleActionClick();
                            }}
                            className="group flex items-center gap-1.5 px-3 py-1.5 sm:py-1 bg-white hover:bg-slate-50 text-slate-900 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all shadow-md active:scale-95 hover:shadow-lg hover:shadow-blue-500/10 border border-white/20"
                        >
                            <span>Découvrir</span>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-900 transition-transform group-hover:translate-x-1 duration-200" />
                        </button>

                        {/* Interactive Close button */}
                        <button
                            onClick={handleClose}
                            className="p-1 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-all active:scale-90"
                            title="Masquer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default PromoBanner;
